import { RECENT_SEARCH_STORAGE_KEY } from '@/src/constants/srp/recentSearch'
import { AirportInfo } from '@/src/types/flight'
import { getCookie, setCookie } from '@/src/utils/browser'
import { validSearchQueryDateFormats } from '@/src/utils/date'
import { compressRecentSearch, decompressRecentSearch } from '@/src/utils/storage/recentSearchCompression'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

dayjs.extend(customParseFormat)

export interface RecentSearchListItem {
    from: string
    to: string
    fromCity: string
    toCity: string
    departDate: string
    returnDate?: string
    numberOfPax: number
    flightClass: string
    adults: number
    childs: number
    infants: number
}

export interface RecentSearchList {
    [key: string]: RecentSearchListItem[]
}

export const useRecentSearch = () => {
    const locale = useLocale()
    const MAX_SEARCHES = 3
    const srpPath = `/${locale}/flights/results`

    const router = useRouter()

    const onRecentSearchClick = useCallback(
        (search: RecentSearchListItem) => () => {
            const { departDate, adults, childs, flightClass, from, to, returnDate, infants } = search
            const params = new URLSearchParams(window.location.search)
            params.set('from', from)
            params.set('to', to)
            params.set('adults', adults.toString())
            params.set('childs', childs.toString())
            params.set('infants', infants.toString())
            params.set('class', flightClass)
            params.set('depart_date', departDate)
            params.set('return_date', returnDate || '')

            const newUrl = `${srpPath}?${params.toString()}`
            router.push(newUrl)
        },
        [router]
    )

    // Helper function to parse date with flexible format
    const parseDate = (dateStr: string) => {
        if (!dateStr?.trim()) return null

        // Try different date formats - all combinations of single/double digit day and month

        for (const format of validSearchQueryDateFormats) {
            const parsed = dayjs(dateStr, format)
            if (parsed.isValid()) {
                return parsed
            }
        }

        return null
    }

    /**
     * Get recent searches from localStorage
     * @returns Array of recent search objects (max 3 items)
     */
    const getRecentSearchFromCookie = useCallback((): RecentSearchList => {
        try {
            if (typeof window === 'undefined') {
                return {}
            }
            const storedData = getCookie(RECENT_SEARCH_STORAGE_KEY)
            if (!storedData) {
                return {}
            }
            let decodedData = storedData
            try {
                decodedData = decodeURIComponent(storedData)
            } catch {
                // If decoding fails (e.g., already decoded or invalid encoding), use raw value
            }
            const parsedData = JSON.parse(decodedData)
            return decompressRecentSearch(parsedData)
        } catch (error) {
            console.error('Error getting recent searches:', error)
            return {}
        }
    }, [])

    /**
     * Set/add a new recent search to localStorage
     * Automatically maintains only the last 3 searches
     * discard duplicate searches
     * @param queryObj - The search query object to store
     */
    const setRecentSearch = useCallback(
        ({ queryObj, airportList }: { queryObj: Record<string, string>; airportList: Record<string, AirportInfo> }) => {
            try {
                if (typeof window === 'undefined') {
                    return
                }

                const { from, to, depart_date, return_date, class: flightClass } = queryObj
                const adults = Number(queryObj?.adults) || 0
                const childs = Number(queryObj?.childs) || 0
                const infants = Number(queryObj?.infants) || 0
                const numberOfPax = adults + childs + infants

                const payload: RecentSearchListItem = {
                    from: from?.toUpperCase() as string,
                    to: to?.toUpperCase() as string,
                    fromCity: airportList[from?.toUpperCase() as string]?.c || '',
                    toCity: airportList[to?.toUpperCase() as string]?.c || '',
                    departDate: depart_date as string,
                    returnDate: return_date as string,
                    numberOfPax,
                    flightClass: flightClass as string,
                    adults,
                    childs,
                    infants
                }

                // Get existing searches
                const existingSearches = getRecentSearchFromCookie()
                const currentLocalExistingSearches = existingSearches[locale] || []

                // Check if the same search already exists
                const isDuplicate = (search: RecentSearchListItem) => {
                    return (
                        search.from === payload.from &&
                        search.to === payload.to &&
                        search.departDate === payload.departDate &&
                        search.returnDate === payload.returnDate &&
                        search.adults === payload.adults &&
                        search.childs === payload.childs &&
                        search.infants === payload.infants
                    )
                }

                // Remove existing duplicate if found
                const filteredSearches = currentLocalExistingSearches.filter(search => !isDuplicate(search))

                // Add new search to the beginning of the array
                const updatedSearches = [payload, ...filteredSearches]

                // Limit to maximum 3 searches
                const limitedSearches = updatedSearches.slice(0, MAX_SEARCHES)

                // Save to localStorage
                // localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(limitedSearches))
                const updatedData = {
                    ...existingSearches,
                    [locale]: limitedSearches
                }
                setCookie(
                    RECENT_SEARCH_STORAGE_KEY,
                    encodeURIComponent(JSON.stringify(compressRecentSearch(updatedData)))
                )
            } catch (error) {
                console.error('Error setting recent search:', error)
            }
        },
        [getRecentSearchFromCookie]
    )

    /**
     * Get filtered recent search list for UI display
     * Filters out past date searches and returns only required keys
     * @returns Array of filtered search objects with required keys only
     */
    const getRecentSearchList = useCallback((): (RecentSearchListItem & { onClick: () => void })[] => {
        try {
            const allSearches = getRecentSearchFromCookie()
            const currentLocalExistingSearches = allSearches[locale] || []
            return currentLocalExistingSearches
                .filter(search => {
                    // Check if departDate exists and is not in the past
                    if (!search.departDate) return false
                    const parsedDepartDate = parseDate(search.departDate)
                    const parsedReturnDate = parseDate(search.returnDate || '')
                    const isDepartDateInPast = parsedDepartDate && parsedDepartDate.isBefore(dayjs(), 'day')
                    const isReturnDateInPast = parsedReturnDate && parsedReturnDate.isBefore(dayjs(), 'day')

                    return !isDepartDateInPast && !isReturnDateInPast
                })
                .map(search => ({
                    ...search,
                    onClick: onRecentSearchClick(search)
                }))
        } catch (error) {
            console.error('Error getting recent search list:', error)
            return []
        }
    }, [getRecentSearchFromCookie, locale])

    return {
        getRecentSearchList,
        setRecentSearch
    }
}
